import type { AdminCategoryFilters, AdminProductFilters, BrandType } from '$lib/types';
import { format } from 'date-fns';
import { defaultAdminProductFilters, defaultProductPaginationSize } from './constants';

const currency = new Intl.NumberFormat('es-MX', {
	style: 'currency',
	currency: 'MXN',

	minimumFractionDigits: 2, // 0 (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
	maximumFractionDigits: 2 // 0 (causes 2500.99 to be printed as $2,501)
});

export const currencyFormat = (amount: number) => currency.format(amount / 100);
export const dateFormat = (date: string | null) => {
	if (!date) return [''];
	return [format(new Date(date), 'yyyy-MM-dd'), format(new Date(date), 'pp')];
};

export const getBrand = (brand?: unknown): BrandType | undefined => {
	if (brand === 'MASTERCARD') return 'MasterCard';
	if (brand === 'AMERICANEXPRESS') return 'American Express';
	if (brand === 'AMERICAN EXPRESS') return 'American Express';

	if (brand !== 'VISA' && brand !== 'MasterCard' && brand !== 'American Express') return undefined;

	return brand;
};

export const getUrlFromProductFilters = (filters: AdminProductFilters): string => {
	const { coincidence, published, lead, archived, categories } = filters;

	let url = '';

	if (coincidence) url += `coincidencia=${coincidence}&`;

	if (published) url += `publicado=true&`;
	if (lead) url += `destacado=true&`;
	if (archived) url += `archivado=true&`;

	if (categories.length > 0) {
		let selectedCategories = 'categorias=';

		// categorias=una,dos,tres...
		categories.forEach((category) => {
			if (category.checked) {
				if (!selectedCategories.endsWith('=')) selectedCategories += ',';
				selectedCategories += category.name;
			}
		});

		url += selectedCategories + '&';
	}

	if (url) url = '?' + url.slice(0, -1);

	return url;
};
export const getProductFiltersFromUrl = (
	searchParams: URLSearchParams,
	allCategories: { name: string }[]
): AdminProductFilters => {
	const filters = defaultAdminProductFilters;

	const coincidence = searchParams.get('coincidencia');
	filters.coincidence = coincidence ?? '';

	const published = searchParams.get('publicado');
	filters.published = !!published;

	const lead = searchParams.get('destacado');
	filters.lead = !!lead;

	const archived = searchParams.get('archivado');
	filters.archived = !!archived;

	const categories = searchParams.get('categorias')?.split(',') ?? [];

	filters.categories = allCategories.map((c) => {
		let checked;
		if (categories.length > 0) {
			checked = categories.includes(c.name);
		} else {
			checked = true;
		}
		return { name: c.name, checked };
	});

	return filters;
};

export const getCategoryFiltersFromUrl = (searchParams: URLSearchParams): AdminCategoryFilters => {
	const filters = defaultAdminProductFilters;

	const coincidence = searchParams.get('coincidencia');
	filters.coincidence = coincidence ?? '';

	const lead = searchParams.get('destacado');
	filters.lead = !!lead;

	const archived = searchParams.get('archivado');
	filters.archived = !!archived;

	return filters;
};
export const getUrlFromCategoryFilters = (filters: AdminCategoryFilters): string => {
	const { name, lead, archived } = filters;
	let url = '';

	if (name) url += `nombre=${name}&`;

	if (lead) url += `destacado=true&`;

	if (archived) url += `archivado=true&`;

	if (url) url = '?' + url.slice(0, -1);

	return url;
};

export const getUrlFromPaginator = (url: URL, updatedParameter: 'cantidad' | 'pagina', newValue: number): string => {
	let newSearch = '';

	switch (updatedParameter) {
		case 'cantidad':
			newSearch = `cantidad=${newValue}&pagina=1`;
			break;

		case 'pagina':
			newSearch = `cantidad=${url.searchParams.get('cantidad') ?? defaultProductPaginationSize}&pagina=${
				newValue + 1
			}`;
			break;
	}

	return `${url.pathname}?${newSearch}`;
};
export const getPaginatorFromUrl = (
	searchParams: URLSearchParams
): { amount: number; start: number; end: number; offset: number } => {
	const amount = Number(searchParams.get('cantidad'));
	const page = Number(searchParams.get('pagina'));

	return {
		amount,
		start: (page - 1) * amount,
		end: page * amount - 1,
		offset: page ? page - 1 : 0
	};
};
